import { useState } from "react";
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from "react-hook-form";
import {
  Alert,
  DatePicker,
  FormStack,
  Input,
  ResponsiveGrid,
  Select,
} from "@marekl/adminer/components";
import { dayjs } from "@marekl/adminer/datetime";
import { MailLink } from "../../../components/SupportContacts";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import { GENERAL_CONTACT } from "../../../lib/contacts";
import { birthNumberHint } from "../../../lib/birthNumber";
import {
  BIRTH_NUMBER_MISSING_HINT,
  MEMBERSHIP_KIND_OPTIONS,
  ROLE_OPTIONS,
  type IdentityField,
  type IdentityLock,
  type MemberFormValues,
} from "./memberFormSchema";
import { MEMBERSHIP_KIND_LABELS } from "../../../lib/memberState";
import { RoleCheckboxes } from "./RoleCheckboxes";
import type { MembershipRole } from "../../../lib/queries/memberships";

const NO_FIELDS: ReadonlySet<IdentityField> = new Set();

export function MemberFormFields({
  register,
  control,
  errors,
  roleOptions = ROLE_OPTIONS,
  showLeaderNote = false,
  identityLock = null,
  lockedIdentity = NO_FIELDS,
  lockRoles = false,
  lockKind = false,
  hideKind = false,
  hideRoles = false,
  kind,
  showCmfData,
}: {
  register: UseFormRegister<MemberFormValues>;
  control: Control<MemberFormValues>;
  errors: FieldErrors<MemberFormValues>;
  roleOptions?: MembershipRole[];
  showLeaderNote?: boolean;
  // identityLock says WHY the identity is restricted, which is what the
  // notice above the fields has to explain; lockedIdentity says WHICH fields
  // it actually reaches. They differ for the ordinary „filled" case, where a
  // member missing a rodné číslo must still be able to get one.
  identityLock?: IdentityLock;
  lockedIdentity?: ReadonlySet<IdentityField>;
  // lockRoles disables the role checkboxes when roster changes are no
  // longer permitted (registration closed for a non-admin).
  lockRoles?: boolean;
  // lockKind pins „Typ členství". It is set for the team leader (who is
  // always a registered member) and once registration closes for a
  // non-admin, since changing the kind then IS a registration change.
  lockKind?: boolean;
  // hideKind drops the control entirely. „Registrace do sezóny" uses it:
  // that workspace registers členy and nothing else, so offering „Nečlen"
  // there would invite exactly the mistake the screen exists to prevent.
  // Non-members are added from the team's soupiska instead.
  hideKind?: boolean;
  // hideRoles drops the „Role v týmu" block. The members registry uses it:
  // it edits an osoba, and roles live on a per-team membership edited on
  // the roster.
  hideRoles?: boolean;
  // kind is the current membership kind, needed to mark the ČNSMS-only fields
  // required and to word the hint. Undefined while the form is untouched.
  kind?: MemberFormValues["membership_kind"];
  // showCmfData overrides whether „Rodné číslo" and the address are RENDERED,
  // leaving kind to decide only whether they are REQUIRED. Default: they
  // follow kind, which is right on the roster, where the dialog creates the
  // kind it names.
  //
  // The members registry passes true. It derives kind from `is_member`, which
  // is scoped to the ACTIVE season, so a nečlen, a loňský člen who has not
  // re-registered, and in the pre_registration phase very nearly everybody
  // read as „guest" and the four fields disappeared — on the one surface
  // makePersonEditSchema calls the designated repair place. A registry that
  // hides the field an admin was sent to fill in is worse than one that only
  // declines to require it.
  showCmfData?: boolean;
}) {
  // Rodné číslo and address are ČNSMS-registration data, so only a „Člen"
  // owes them. Written as „anything but a guest" this also claimed them
  // from a „Zahraniční člen", marking four fields required that neither the
  // schema (requireFullDataForMembers) nor the server asks a club outside
  // ČNSMS for, and that its public registration form deliberately never
  // shows.
  const isMember = kind === "registered";
  // Rodné číslo a adresa jsou údaje ČNSMS: patří k registraci do sezóny a
  // nikam jinam. Nečlen ani zahraniční člen se do sezóny neregistrují, takže
  // ta pole u nich nejsou jen nepovinná, ona nedávají smysl. Nečlena navíc
  // zakládá klub, který do sezóny registrovaný není (ErrTeamNotRegistered);
  // ptát se ho na rodné číslo znamenalo ptát se na údaj, který systém v ten
  // okamžik odmítá použít.
  //
  // Povýšení na člena tím netrpí: EditMemberModal předává živě vybraný druh
  // členství, takže se pole objeví ve chvíli, kdy správce zvolí „Člen".
  //
  // Registr členské základny si to přebíjí přes showCmfData: tam se pole
  // opravují, ne zakládají.
  const wantsCmfData = showCmfData ?? isMember;

  // A live WARNING, deliberately not an error: around 6% of the numbers
  // already on file fail one of these checks, across a third of the clubs,
  // so refusing here would strand people whose leader cannot fix them
  // anyway (the identity lock). The point is to catch a typo while the
  // person who made it is still looking at it. The one place this does
  // block is the team leader, server-side, where the lock makes a bad
  // number permanent. See docs/rodne-cislo.md §5.
  const watchedBirthNumber = useWatch({ control, name: "person.birth_number" });
  const watchedBirthDate = useWatch({ control, name: "person.birth_date" });
  // Not shown for a nečlen, on purpose: a guest owes no rodné číslo at all
  // (the field stays rendered and optional), so a warning about the one they
  // volunteered would be the app taking an interest in a number it does not
  // require.
  // Judged on blur, not per keystroke: while the field has focus a
  // half-typed number is not a wrong one. See birthNumberHint.
  const [rcTyping, setRcTyping] = useState(false);
  const rcField = register("person.birth_number");
  // Gated on wantsCmfData, not on isMember: on the roster the two are the same
  // value, but the registry renders the field for a nečlen precisely so an
  // admin can repair it, and a repair surface without the live check is the
  // one place the warning is most worth having.
  // An EMPTY number of a člen is said too, as the same kind of warning: a
  // registration accepts it for now (REQUIRED_DATA_FIELDS), but ČNSMS still
  // collects it, so the gap should be visible rather than silently allowed.
  const rcWarning =
    wantsCmfData && !errors.person?.birth_number
      ? (birthNumberHint(
          watchedBirthNumber ?? "",
          watchedBirthDate ? watchedBirthDate.format("YYYY-MM-DD") : undefined,
          { typing: rcTyping },
        ) ??
        (isMember && !(watchedBirthNumber ?? "").trim()
          ? BIRTH_NUMBER_MISSING_HINT
          : undefined))
      : undefined;

  return (
    <FormStack>
      {!hideKind && (
        <Select
          label="Typ členství"
          required
          disabled={lockKind}
          error={errors.membership_kind?.message}
          hint={
            isMember
              ? "Řádná registrace do sezóny, může na MČR. Vyžaduje rodné číslo a adresu."
              : "Nečlen pro pohárové soutěže, není registrovaný v ČNSMS. Nelze přihlásit na MČR."
          }
          {...register("membership_kind")}
        >
          {MEMBERSHIP_KIND_OPTIONS.map((kind) => (
            <option key={kind} value={kind}>
              {MEMBERSHIP_KIND_LABELS[kind]}
            </option>
          ))}
        </Select>
      )}
      {lockedIdentity.size > 0 && (
        <Alert variant="warn">
          {identityLock === "leader" ? (
            // The leader's identity is locked year-round, so the reason is
            // not the registration window and must not be worded as such.
            <>
              U vedoucího lze upravovat pouze kontaktní údaje. Jméno, příjmení,
              datum narození a rodné číslo změní jen správce členské základny;
              kontaktujte ČNSMS na <MailLink to={GENERAL_CONTACT} />.
            </>
          ) : identityLock === "closed" ? (
            <>
              Po uzavření registrace lze upravovat pouze kontaktní údaje. Jméno,
              příjmení, datum narození a rodné číslo změní jen správce členské
              základny; kontaktujte ČNSMS na <MailLink to={GENERAL_CONTACT} />.
            </>
          ) : (
            <>
              Jméno, příjmení, datum narození a rodné číslo, které už jsou
              vyplněné, změní jen správce členské základny; kontaktujte ČNSMS na{" "}
              <MailLink to={GENERAL_CONTACT} />.
              Chybějící údaj můžete doplnit sami.
            </>
          )}
        </Alert>
      )}
      <ResponsiveGrid columns={2}>
        <Input
          label="Jméno"
          required
          disabled={lockedIdentity.has("first_name")}
          error={errors.person?.first_name?.message}
          {...register("person.first_name")}
        />
        <Input
          label="Příjmení"
          required
          disabled={lockedIdentity.has("last_name")}
          error={errors.person?.last_name?.message}
          {...register("person.last_name")}
        />
      </ResponsiveGrid>
      <ResponsiveGrid columns={3}>
        <Controller
          control={control}
          name="person.birth_date"
          render={({ field, fieldState }) => (
            <DatePicker
              label="Datum narození"
              required
              disabled={lockedIdentity.has("birth_date")}
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDate={dayjs()}
              error={fieldState.error?.message}
            />
          )}
        />
        {wantsCmfData && (
          <Input
            label="Rodné číslo"
            disabled={lockedIdentity.has("birth_number")}
            error={errors.person?.birth_number?.message}
            warning={rcWarning}
            {...rcField}
            onFocus={() => setRcTyping(true)}
            onBlur={(e) => {
              setRcTyping(false);
              // react-hook-form needs its own onBlur for mode: "onTouched".
              void rcField.onBlur(e);
            }}
          />
        )}
        <Select
          label="Státní příslušnost"
          required
          error={errors.person?.country_code?.message}
          {...register("person.country_code")}
        >
          {COUNTRY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </ResponsiveGrid>
      {wantsCmfData && (
        <Input
          label="Ulice a číslo popisné"
          required={isMember}
          error={errors.person?.street?.message}
          {...register("person.street")}
        />
      )}
      <ResponsiveGrid columns={3}>
        {wantsCmfData && (
          <Input
            label="Město"
            required={isMember}
            error={errors.person?.city?.message}
            {...register("person.city")}
          />
        )}
        {wantsCmfData && (
          <Input
            label="PSČ"
            required={isMember}
            error={errors.person?.postal_code?.message}
            {...register("person.postal_code")}
          />
        )}
        <Input label="Telefon" error={errors.person?.phone?.message} {...register("person.phone")} />
      </ResponsiveGrid>
      {!hideRoles && (
        <Controller
          control={control}
          name="roles"
          render={({ field, fieldState }) => (
            <RoleCheckboxes
              value={field.value}
              onChange={field.onChange}
              options={roleOptions}
              showLeaderNote={showLeaderNote}
              disabled={lockRoles}
              error={fieldState.error?.message}
            />
          )}
        />
      )}
    </FormStack>
  );
}
